import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { motion, useInView } from "framer-motion";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Users, AlertTriangle, Coffee, Flame } from "lucide-react";
import axios from "axios";
import { toast } from "../hooks/use-toast";
import { API } from "../lib/api";

const CATEGORIES = [
  { value: "entrees", labelKey: "menu.cat_entrees" },
  { value: "plats", labelKey: "menu.cat_plats" },
  { value: "garnitures", labelKey: "menu.cat_garnitures" },
  { value: "desserts", labelKey: "menu.cat_desserts" },
  { value: "brasero", labelKey: "menu.cat_brasero" },
  { value: "boissons", labelKey: "menu.cat_boissons" },
  { value: "allergenes", labelKey: "menu.cat_allergenes" },
];

const DRINKS = {
  aperitifs: ["aper_kir", "aper_kir_royal", "aper_pastis", "aper_martini", "aper_campari", "aper_suze", "aper_porto"],
  cocktails: ["ckt_spritz", "ckt_limoncello", "ckt_negroni", "ckt_americano", "ckt_suze_tonic", "ckt_gin_tonic", "ckt_cuba_libre", "ckt_caipirinha"],
  wines_by_glass: ["wbg_champagne", "wbg_petnat", "wbg_white", "wbg_rose_ventoux", "wbg_rose_provence", "wbg_red", "wbg_moelleux"],
  waters: ["water_vittel", "water_badoit", "water_pellegrino"],
  softs: ["soft_coca", "soft_coca_zero", "soft_schweppes", "soft_orangina", "soft_lemonade", "soft_icetea", "soft_pago", "soft_syrup"],
  cafes: ["cafe_simple", "cafe_petit", "cafe_allonge", "cafe_creme", "cafe_noisette", "cafe_grand_creme", "cafe_grand_noisette", "cafe_lait_supp"],
  teas: ["the_darjeeling", "the_earl_grey", "the_sencha", "the_assam", "the_ceylan", "tisane_camomille", "tisane_verveine", "tisane_menthe", "tisane_fruits_rouges", "chocolat_chaud"],
};

const MenuItemCard = ({ item, index }) => {
  const { t } = useTranslation();
  return (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay: index * 0.08 }}
    className="menu-line relative flex items-start justify-between border-b border-[#D8D0C5] pb-5 mb-5 
               group hover:bg-[#F5E6D3]/30 p-4 rounded-lg transition-colors duration-300"
    data-testid={`menu-item-${item.id || index}`}
  >
    <div className="flex-1 pr-4">
      <div className="flex items-center gap-3">
        <h4
          className="text-xl md:text-2xl font-normal text-[#2C2420] group-hover:text-[#C1624D] transition-colors duration-300"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          {item.name}
        </h4>
        {item.is_shared && (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 text-xs uppercase tracking-wider 
                       bg-[#6F7D4E]/10 text-[#6F7D4E] rounded-full"
            style={{ fontFamily: "'Lato', sans-serif" }}
            data-testid={`shared-badge-${item.id || index}`}
          >
            <Users size={12} />
            {t("menu.to_share")}
          </span>
        )}
      </div>
      <p
        className="text-sm md:text-base text-[#8C847F] mt-2 leading-relaxed"
        style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}
      >
        {item.description}
      </p>
    </div>
    {item.price && (
      <div className="text-right flex-shrink-0">
        <span
          className="text-lg text-[#C1624D] font-light whitespace-nowrap"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          {item.price}
        </span>
        {item.is_shared && (
          <p
            className="text-[10px] uppercase tracking-wider text-[#8C847F] mt-0.5"
            style={{ fontFamily: "'Lato', sans-serif" }}
          >
            pour 2 pers.
          </p>
        )}
      </div>
    )}
  </motion.div>
  );
};

const ALLERGENES = [
  { name: "Gluten", desc: "Blé, seigle, orge, avoine, épeautre et produits dérivés" },
  { name: "Crustacés", desc: "Crevettes, crabes, homards et produits à base de crustacés" },
  { name: "Œufs", desc: "Œufs et produits à base d'œufs" },
  { name: "Poissons", desc: "Poissons et produits à base de poissons" },
  { name: "Arachides", desc: "Cacahuètes et produits à base d'arachides" },
  { name: "Soja", desc: "Soja et produits à base de soja" },
  { name: "Lait", desc: "Lait et produits à base de lait (y compris lactose)" },
  { name: "Fruits à coque", desc: "Amandes, noisettes, noix, noix de cajou, pistaches…" },
  { name: "Céleri", desc: "Céleri et produits à base de céleri" },
  { name: "Moutarde", desc: "Moutarde et produits à base de moutarde" },
  { name: "Graines de sésame", desc: "Graines de sésame et produits à base de sésame" },
  { name: "Sulfites", desc: "Anhydride sulfureux et sulfites (> 10mg/kg ou 10mg/L)" },
  { name: "Lupin", desc: "Lupin et produits à base de lupin" },
  { name: "Mollusques", desc: "Moules, huîtres, escargots, calamars et produits dérivés" },
];

const AllergenesContent = () => {
  const { t } = useTranslation();
  return (
  <div data-testid="allergenes-content">
    {/* Warning note */}
    <div className="flex items-start gap-4 p-6 bg-[#C1624D]/8 border border-[#C1624D]/20 rounded-sm mb-8">
      <AlertTriangle size={22} className="text-[#C1624D] flex-shrink-0 mt-0.5" />
      <div>
        <p
          className="text-base font-medium text-[#2C2420] mb-2"
          style={{ fontFamily: "'Lato', sans-serif" }}
          data-testid="allergenes-warning"
        >
          {t("menu.allergenes_info_title")}
        </p>
        <p
          className="text-sm text-[#5D544F] leading-relaxed"
          style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}
        >
          {t("menu.allergenes_info")}
        </p>
      </div>
    </div>

    {/* Allergens list */}
    <p
      className="text-xs uppercase tracking-[0.2em] text-[#8C847F] mb-6"
      style={{ fontFamily: "'Lato', sans-serif" }}
    >
      {t("menu.allergenes_title")}
    </p>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
      {ALLERGENES.map((a, i) => (
        <div
          key={a.name}
          className="flex items-start gap-3 py-3 border-b border-[#D8D0C5]/60 last:border-b-0"
          data-testid={`allergene-${i}`}
        >
          <span
            className="w-6 h-6 rounded-full bg-[#F5E6D3] flex items-center justify-center text-xs text-[#C1624D] flex-shrink-0 mt-0.5"
            style={{ fontFamily: "'Lato', sans-serif", fontWeight: 700 }}
          >
            {i + 1}
          </span>
          <div>
            <p className="text-sm font-medium text-[#2C2420]" style={{ fontFamily: "'Lato', sans-serif" }}>
              {a.name}
            </p>
            <p
              className="text-xs text-[#8C847F]"
              style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}
            >
              {a.desc}
            </p>
          </div>
        </div>
      ))}
    </div>

    {/* Bottom note */}
    <div className="mt-8 p-5 bg-white/60 border border-[#D8D0C5]/50 rounded-sm text-center">
      <p
        className="text-sm text-[#5D544F] italic"
        style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1rem" }}
      >
        {t("menu.allergenes_quote")}
      </p>
    </div>
  </div>
  );
};

const DrinksSection = ({ title, items, accent }) => {
  const { t } = useTranslation();
  return (
    <div className="mb-10 last:mb-0">
      <p
        className="text-xs uppercase tracking-[0.2em] mb-4"
        style={{ color: accent, fontFamily: "'Lato', sans-serif", fontWeight: 500 }}
      >
        {title}
      </p>
      <ul className="space-y-2.5">
        {items.map((id) => (
          <li
            key={id}
            className="flex flex-col sm:flex-row sm:items-baseline sm:gap-3 py-1"
            data-testid={`drink-${id}`}
          >
            <span
              className="text-base text-[#2C2420] font-normal"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              {t(`menu.drinks.${id}.name`)}
            </span>
            <span
              className="hidden sm:block flex-1 border-b border-dotted border-[#D8D0C5]"
              style={{ minHeight: "1px" }}
            />
            <span
              className="text-sm text-[#5D544F]"
              style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}
            >
              {t(`menu.drinks.${id}.desc`)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const DrinksContent = () => {
  const { t } = useTranslation();
  return (
    <div data-testid="drinks-content">
      {/* Intro */}
      <div className="flex items-start gap-4 p-6 bg-[#6F7D4E]/8 border border-[#6F7D4E]/20 rounded-sm mb-10">
        <Coffee size={22} className="text-[#6F7D4E] flex-shrink-0 mt-0.5" />
        <div>
          <p
            className="text-base font-medium text-[#2C2420] mb-2"
            style={{ fontFamily: "'Lato', sans-serif" }}
          >
            {t("menu.drinks_intro_title")}
          </p>
          <p
            className="text-sm text-[#5D544F] leading-relaxed"
            style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}
          >
            {t("menu.drinks_intro_desc")}
          </p>
        </div>
      </div>

      <DrinksSection
        title={t("menu.drinks_aperitifs_title")}
        items={DRINKS.aperitifs}
        accent="#A6503C"
      />
      <DrinksSection
        title={t("menu.drinks_cocktails_title")}
        items={DRINKS.cocktails}
        accent="#6F7D4E"
      />
      <DrinksSection
        title={t("menu.drinks_wines_title")}
        items={DRINKS.wines_by_glass}
        accent="#8B3A3A"
      />
      <DrinksSection
        title={t("menu.drinks_waters_title")}
        items={DRINKS.waters}
        accent="#A5C4D4"
      />
      <DrinksSection
        title={t("menu.drinks_softs_title")}
        items={DRINKS.softs}
        accent="#C1624D"
      />
      <DrinksSection
        title={t("menu.drinks_cafes_title")}
        items={DRINKS.cafes}
        accent="#8B6F4E"
      />
      <DrinksSection
        title={t("menu.drinks_teas_title")}
        items={DRINKS.teas}
        accent="#6F7D4E"
      />

      <div className="mt-8 p-5 bg-white/60 border border-[#D8D0C5]/50 rounded-sm text-center">
        <p
          className="text-sm text-[#5D544F] italic"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1rem" }}
        >
          {t("menu.drinks_quote")}
        </p>
      </div>
    </div>
  );
};


const BraseroContent = () => {
  const { t } = useTranslation();
  return (
    <div data-testid="brasero-content">
      <div className="flex items-start gap-4 p-6 bg-[#C1624D]/8 border border-[#C1624D]/20 rounded-sm mb-10">
        <Flame size={22} className="text-[#C1624D] flex-shrink-0 mt-0.5" />
        <div>
          <p
            className="text-base font-medium text-[#2C2420] mb-2"
            style={{ fontFamily: "'Lato', sans-serif" }}
          >
            {t("menu.brasero_intro_title")}
          </p>
          <p
            className="text-sm text-[#5D544F] leading-relaxed"
            style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}
          >
            {t("menu.brasero_intro_desc")}
          </p>
        </div>
      </div>

      <p
        className="text-xs uppercase tracking-[0.2em] text-[#8C847F] mb-6"
        style={{ fontFamily: "'Lato', sans-serif" }}
      >
        {t("events.brasero_formulas_title")}
      </p>
      {[1, 2, 3, 4].map((n) => {
        const [name, price] = t(`events.brasero_formula_${n}`).split(" — ");
        return (
          <div
            key={n}
            className="flex items-baseline justify-between border-b border-[#D8D0C5] pb-5 mb-5 p-4 rounded-lg hover:bg-[#F5E6D3]/30 transition-colors duration-300"
            data-testid={`menu-brasero-formula-${n}`}
          >
            <h4
              className="text-xl md:text-2xl font-normal text-[#2C2420]"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              {name}
            </h4>
            <span
              className="text-lg text-[#C1624D] font-light whitespace-nowrap pl-4"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              {price}
            </span>
          </div>
        );
      })}

      <div className="mt-8 p-5 bg-white/60 border border-[#D8D0C5]/50 rounded-sm text-center">
        <p
          className="text-sm text-[#5D544F] italic"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1rem" }}
        >
          {t("events.brasero_formula_note")}
        </p>
      </div>
    </div>
  );
};

const MENU_CACHE_KEY = "ap_menu_v1";

const MenuItemSkeleton = ({ index }) => (
  <div
    className="flex items-start justify-between border-b border-[#D8D0C5] pb-5 mb-5 p-4 animate-pulse"
    data-testid={`menu-skeleton-${index}`}
  >
    <div className="flex-1 pr-4">
      <div className="h-6 bg-[#D8D0C5]/60 rounded w-2/3 mb-3" />
      <div className="h-3 bg-[#D8D0C5]/40 rounded w-full mb-2" />
      <div className="h-3 bg-[#D8D0C5]/40 rounded w-5/6" />
    </div>
    <div className="h-5 bg-[#D8D0C5]/60 rounded w-12 flex-shrink-0" />
  </div>
);

export const MenuSection = () => {
  const { t } = useTranslation();
  // Hydrate immediately from localStorage to avoid the perceived loading delay
  const [menuItems, setMenuItems] = useState(() => {
    try {
      const cached = localStorage.getItem(MENU_CACHE_KEY);
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });
  const [activeTab, setActiveTab] = useState("entrees");
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const res = await axios.get(`${API}/menu`);
        const items = res.data.items;
        setMenuItems(items);
        try {
          localStorage.setItem(MENU_CACHE_KEY, JSON.stringify(items));
        } catch {
          // ignore quota / privacy mode errors
        }
      } catch (err) {
        // Only surface a toast if we have NOTHING to show
        if (menuItems.length === 0) {
          toast({
            variant: "destructive",
            title: t("toast.error_loading_title"),
            description: t("toast.error_loading_menu"),
          });
        }
      }
    };
    fetchMenu();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredItems = menuItems.filter((item) => item.category === activeTab);

  return (
    <section
      id="carte"
      data-testid="menu-section"
      className="py-24 md:py-36 px-6 md:px-12 bg-[#F5E6D3]/40"
    >
      <div className="max-w-4xl mx-auto" ref={ref}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <p
            className="text-sm uppercase tracking-[0.3em] text-[#C1624D] mb-4"
            style={{ fontFamily: "'Lato', sans-serif" }}
          >
            {t("menu.label")}
          </p>
          <h2
            className="text-4xl md:text-5xl font-light tracking-tight text-[#2C2420] mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
            data-testid="menu-heading"
          >
            {t("menu.heading")}
          </h2>
          <div className="ornament-divider max-w-xs mx-auto">
            <span className="text-[#C1624D] text-2xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>&bull;</span>
          </div>
          <p
            className="text-base text-[#8C847F] mt-4 max-w-lg mx-auto"
            style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}
          >
            {t("menu.garnitures_note")}
          </p>
        </motion.div>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.2 }}
        >
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList
              className="w-full max-w-3xl mx-auto flex flex-wrap justify-center sm:justify-start bg-transparent h-auto p-0 mb-10 md:mb-12 gap-x-2 gap-y-2 sm:gap-0 sm:border-b border-[#D8D0C5] rounded-none"
              data-testid="menu-tabs"
            >
              {CATEGORIES.map((cat) => (
                <TabsTrigger
                  key={cat.value}
                  value={cat.value}
                  data-testid={`menu-tab-${cat.value}`}
                  className="basis-[calc(33.333%-8px)] sm:basis-auto sm:flex-shrink-0 sm:px-4 pb-2 sm:pb-3 rounded-none text-[11px] sm:text-sm md:text-base uppercase tracking-[0.08em] sm:tracking-[0.15em] 
                             text-[#8C847F] border-b-2 border-[#D8D0C5] sm:border-transparent
                             data-[state=active]:text-[#C1624D] data-[state=active]:border-[#C1624D]
                             data-[state=active]:bg-transparent data-[state=active]:shadow-none
                             hover:text-[#5D544F] transition-colors duration-300 whitespace-normal h-auto min-h-[36px] leading-tight text-center"
                  style={{ fontFamily: "'Lato', sans-serif", fontWeight: 400 }}
                >
                  {t(cat.labelKey)}
                </TabsTrigger>
              ))}
            </TabsList>

            {CATEGORIES.map((cat) => (
              <TabsContent key={cat.value} value={cat.value} data-testid={`menu-content-${cat.value}`}>
                {cat.value === "allergenes" ? (
                  <AllergenesContent />
                ) : cat.value === "boissons" ? (
                  <DrinksContent />
                ) : cat.value === "brasero" ? (
                  <BraseroContent />
                ) : (
                  <div>
                    {filteredItems.length > 0
                      ? filteredItems.map((item, i) => (
                          <MenuItemCard key={item.id || item.name} item={item} index={i} />
                        ))
                      : Array.from({ length: 4 }).map((_, i) => (
                          <MenuItemSkeleton key={`sk-${i}`} index={i} />
                        ))}
                  </div>
                )}
              </TabsContent>
            ))}
          </Tabs>
        </motion.div>
      </div>
    </section>
  );
};
